import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, BadgeCheck } from 'lucide-react';
import getFileURL from '../../utils/fileURL';

// SINGLE-COLUMN COLLAPSIBLE SIDEBAR (navigation redesign).
//
// Replaces the old icon RAIL + detail PANEL. One scrolling column: brand, a
// user card (avatar + name + role pill), then the menu. Solo items render
// directly; every other menu is a COLLAPSIBLE SECTION with an uppercase mono
// header (label + item count + chevron). Every section starts OPEN (owner's
// call); a header click collapses it and the choice sticks per section.
//
// PRESENTATION ONLY. What the menu contains, and every gate behind it, is the
// caller's problem (Sidebar.jsx) — this component draws whatever model it is
// handed, so the gating logic stays in exactly one place.
//
// Props:
//   brandLogo   image src for the brand mark
//   user        { name, image, roleLabel }
//   showBadge   verified tick on the avatar
//   solo        [{ id, name, path, icon, activePaths?, subItems? }]
//   sections    [{ id, title, items: [{ id, name, path, icon, activePaths?, badge? }] }]
//   isOpen      drawer open. On a phone that is the drawer itself (closed =
//               off-screen). On desktop closed means the COLLAPSED RAIL: a
//               4rem icon column, not a hidden sidebar — this component drew
//               `w-0 -translate-x-full` at every width, so the menu vanished
//               completely when the hamburger was pressed (owner report,
//               2026-09-09). The flat Sidebar and the patient app always had
//               the rail; only this one, which every tenant role actually
//               sees, did not.
//   onNavigate  (event, path) => void — dirty-form guard + mobile close
//   onProfile   () => void — user-card click → profile
//   innerRef    AppLayout's outside-click ref (Sidebar is a forwardRef)

const TEAL = '#4ABDBD';
const DEEP = '#2D8A8A';

export default function CollapsibleSidebar({
    brandLogo,
    user,
    showBadge,
    solo = [],
    sections = [],
    isOpen,
    onNavigate,
    onProfile,
    innerRef,
}) {
    const location = useLocation();
    // A page opened FROM another screen keeps that screen lit (owner, 20 Oct
    // 2026: the doctor profile's Payments ledger / Subscription light Doctors,
    // not nothing). The opener says so in `state.navFrom` (common/BackToOrigin).
    const currentPath = location.state?.navFrom || location.pathname;

    const itemMatches = (item) =>
        (item.activePaths ? item.activePaths.includes(currentPath) : currentPath === item.path) ||
        (item.subItems ? item.subItems.some((s) => (s.activePaths ? s.activePaths.includes(currentPath) : currentPath === s.path)) : false);

    // Per-section open overrides; a click flips it. Keyed by section id.
    const [overrides, setOverrides] = useState({});
    // Every section starts OPEN (owner's call — a menu that must be opened
    // before it can be read hides the product); a header click still sticks.
    const isExpanded = (s) =>
        Object.prototype.hasOwnProperty.call(overrides, s.id) ? overrides[s.id] : true;
    const toggle = (s) =>
        setOverrides((prev) => ({ ...prev, [s.id]: !isExpanded(s) }));

    const initial = (user?.name || '?').replace(/^Dr\.?\s+/i, '').charAt(0).toUpperCase();

    const itemRow = (item) => {
        const active = itemMatches(item);
        const Icon = item.icon;
        return (
            <NavLink
                key={item.id}
                to={item.path}
                data-testid={item.id}
                onClick={(e) => onNavigate(e, item.path)}
                title={!isOpen ? item.name : undefined}
                className={`group relative flex items-center rounded-[11px] text-[13px] font-medium min-w-0 transition-colors ${
                    isOpen ? 'gap-3 px-3 py-2.5' : 'justify-center px-0 py-2.5'
                } ${active ? 'text-white' : 'text-gray-600 hover:bg-[#4ABDBD]/10 hover:text-[#2D8A8A]'}`}
            >
                {/* Shared-element pill — one layoutId across the whole column,
                    so clicking another entry SLIDES the gradient there (the
                    Super Admin sidebar's exact spring) instead of blinking. */}
                {active && (
                    <motion.div
                        layoutId="tenant-sidebar-active"
                        className="absolute inset-0 rounded-[11px]"
                        style={{ background: `linear-gradient(90deg, ${TEAL}, ${DEEP})`, boxShadow: '0 8px 20px -10px rgba(45,138,138,.55)' }}
                        initial={false}
                        transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                )}
                {Icon && (
                    <Icon className={`relative z-10 w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-white' : 'text-gray-400 group-hover:text-[#2D8A8A]'}`} />
                )}
                {isOpen && <span className="relative z-10 truncate flex-1">{item.name}</span>}
                {/* Optional count (e.g. unseen shared visits). Gold, the
                    brand accent, so it reads on the teal active pill too;
                    collapsed it shrinks to a dot on the icon. */}
                {item.badge > 0 && (isOpen ? (
                    <span data-testid={`${item.id}-badge`} className="relative z-10 rounded-full bg-[#D7A05C] px-1.5 py-px text-[10px] font-bold text-white">
                        {item.badge > 99 ? '99+' : item.badge}
                    </span>
                ) : (
                    <span data-testid={`${item.id}-badge`} className="absolute right-2 top-1.5 z-10 h-2 w-2 rounded-full bg-[#D7A05C]" aria-hidden="true" />
                ))}
            </NavLink>
        );
    };

    // A solo item that carries a dropdown (patient's Doctor Library) renders as
    // a small inline group: an icon+label header, its sub-links indented below.
    // The parent's own route equals its first sub-link, so nothing is lost.
    const soloRow = (item) => {
        // Collapsed, an inline group has no room for a label or an indented
        // sub-list; the parent's own route is its first sub-link, so the icon
        // row alone loses nothing.
        if (item.subItems && item.subItems.length && !isOpen) {
            return itemRow({ ...item, path: item.subItems[0].path });
        }
        if (item.subItems && item.subItems.length) {
            const Icon = item.icon;
            return (
                <div key={item.id} className="min-w-0">
                    <div
                        data-testid={item.id}
                        className="flex items-center gap-3 px-3 py-2.5 rounded-[11px] text-[13px] font-medium text-gray-600 min-w-0"
                    >
                        {Icon && <Icon className="w-[18px] h-[18px] flex-shrink-0 text-gray-400" />}
                        <span className="truncate flex-1">{item.name}</span>
                    </div>
                    <div className="ml-9 flex flex-col gap-0.5">
                        {item.subItems.map((sub) => {
                            const active = sub.activePaths ? sub.activePaths.includes(currentPath) : currentPath === sub.path;
                            return (
                                <NavLink
                                    key={sub.id}
                                    to={sub.path}
                                    data-testid={sub.id}
                                    onClick={(e) => onNavigate(e, sub.path)}
                                    className={`block px-3 py-2 rounded-[10px] text-[12.5px] truncate ${
                                        active ? 'text-[#2D8A8A] font-semibold bg-[#4ABDBD]/10' : 'text-gray-600 hover:bg-[#4ABDBD]/10 hover:text-[#2D8A8A]'
                                    }`}
                                >
                                    {sub.name}
                                </NavLink>
                            );
                        })}
                    </div>
                </div>
            );
        }
        return itemRow(item);
    };

    return (
        <div
            ref={innerRef}
            data-testid="collapsible-sidebar"
            className={`fixed inset-y-0 left-0 z-50 bg-white transform transition-all duration-300 shadow-lg flex flex-col h-screen overflow-hidden
                ${isOpen ? 'translate-x-0 w-64' : '-translate-x-full w-0 lg:translate-x-0 lg:w-16'} lg:static`}
        >
            {/* Brand */}
            {/* The Super Admin's exact brand row (owner's pick over the old
                gradient-tile treatment): bare 40px heart, 20px plain-teal
                wordmark. Keep the three apps saying the same thing here. */}
            <div className={`flex items-center py-3 flex-shrink-0 ${isOpen ? 'px-4 gap-2' : 'px-1 justify-center'}`}>
                <img src={brandLogo} alt="Mitra One Care" className={`object-contain flex-shrink-0 ${isOpen ? 'h-10 w-10' : 'h-9 w-9'}`} />
                {isOpen && (
                    <span className="text-xl font-bold leading-none text-[#4ABDBD] whitespace-nowrap">Mitra One Care</span>
                )}
            </div>

            {/* User card */}
            <button
                type="button"
                onClick={onProfile}
                data-testid="sidebar-user-card"
                title={!isOpen ? user?.name : undefined}
                className={`relative mb-2.5 mt-1 rounded-2xl flex items-center text-left flex-shrink-0 transition-shadow hover:shadow-md ${
                    isOpen ? 'mx-3.5 p-3 gap-3' : 'mx-2 p-1.5 justify-center'}`}
                style={{ background: 'linear-gradient(135deg, rgba(74,189,189,.12), rgba(58,155,155,.10))', border: '1px solid rgba(74,189,189,.28)' }}
            >
                {user?.image && String(user.image).trim() !== '' ? (
                    <img
                        src={getFileURL(user.image)}
                        alt={user.name}
                        className={`rounded-full object-cover flex-shrink-0 border-2 border-white ${isOpen ? 'w-[46px] h-[46px]' : 'w-9 h-9'}`}
                        style={{ boxShadow: '0 6px 14px -6px rgba(45,138,138,.7)' }}
                    />
                ) : (
                    <span
                        className={`rounded-full grid place-items-center flex-shrink-0 text-white font-bold border-2 border-white ${isOpen ? 'w-[46px] h-[46px] text-[19px]' : 'w-9 h-9 text-[15px]'}`}
                        style={{ background: `linear-gradient(135deg, ${TEAL}, ${DEEP})`, boxShadow: '0 6px 14px -6px rgba(45,138,138,.7)' }}
                    >
                        {initial}
                    </span>
                )}
                {isOpen && (
                    <span className="min-w-0 flex-1">
                        <span className="block font-bold text-[13.5px] text-[#16323A] truncate">{user?.name}</span>
                        <span className="mt-0.5 inline-block text-[10.5px] font-semibold text-[#2D8A8A] bg-white px-2.5 py-0.5 rounded-lg shadow-sm">
                            {user?.roleLabel}
                        </span>
                    </span>
                )}
                {showBadge && isOpen && (
                    <BadgeCheck className="absolute top-2 right-2 w-5 h-5 text-white" fill="#10B981" />
                )}
            </button>

            {/* Nav */}
            <nav className={`flex-1 overflow-y-auto overflow-x-hidden pt-0.5 pb-4 min-w-0 ${isOpen ? 'px-3' : 'px-2'}`}>
                {solo.length > 0 && (
                    <div className="flex flex-col gap-0.5 mt-0.5 mb-1">
                        {solo.map(soloRow)}
                    </div>
                )}

                {sections.map((s) => {
                    const expanded = isExpanded(s);
                    // ON THE RAIL a section header cannot be read, and
                    // collapsing what you cannot read only hides icons — so
                    // the header becomes a hairline and every item stays.
                    if (!isOpen) {
                        return (
                            <div key={s.id} className="mt-1.5" data-testid={`sidebar-section-${s.id}`}>
                                <div className="mx-2 my-1.5 border-t border-gray-100" />
                                <div className="flex flex-col gap-0.5">
                                    {s.items.map(itemRow)}
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div key={s.id} className="mt-1.5">
                            <button
                                type="button"
                                onClick={() => toggle(s)}
                                data-testid={`sidebar-section-${s.id}`}
                                className="w-full flex items-center gap-2 px-2.5 pt-2 pb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-gray-400 hover:text-[#2D8A8A]"
                            >
                                <span className="truncate">{s.title}</span>
                                <span className="text-[9px] text-gray-400 bg-gray-100 rounded-md px-1.5 py-px leading-none">{s.items.length}</span>
                                <ChevronDown className={`ml-auto w-[13px] h-[13px] flex-shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`} />
                            </button>
                            {expanded && (
                                <div className="flex flex-col gap-0.5">
                                    {s.items.map(itemRow)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>
        </div>
    );
}
